/**
 * Setting up the FINN Outreach Sender, explained for people who have never installed a Chrome
 * extension by hand (Sofia, Oct 20 2026: "the users are not tech people"). Shown in step 2 (Find
 * people), where the extension is first needed, and in step 3 (Write & send).
 *
 * Three states: not installed → numbered steps with what you will see at each one; installed but
 * older than the one People Space ships → how to update; current → one green line.
 */
import { useState } from 'react';
import { EXTENSION_ZIP, LATEST_EXTENSION_VERSION, isOutdated } from './outreachExtension';

function CopyAddress() {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <code className="rounded bg-white px-1.5 py-0.5 text-[#1A1A1A]">chrome://extensions</code>
      <button
        type="button"
        className="rounded-md border border-solid border-[#1A1A1A] bg-white px-2 py-0.5 text-xs font-semibold text-[#1A1A1A]"
        onClick={() => {
          void navigator.clipboard?.writeText('chrome://extensions').then(() => setCopied(true));
        }}
      >
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </span>
  );
}

const Step = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <li className="flex gap-3">
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#1A1A1A] text-xs font-semibold text-white">{n}</span>
    <span className="pt-0.5">{children}</span>
  </li>
);

export function ExtensionSetup({ version }: { version: string | null | undefined }) {
  if (version === undefined) return <p className="m-0 text-[13px] text-[#707070]">Checking whether the FINN Outreach Sender is installed…</p>;

  if (version && !isOutdated(version)) {
    return (
      <p className="m-0 text-[13px] text-[#1D9247]">
        ✓ The FINN Outreach Sender is installed and ready (version {version}). Stay signed in to LinkedIn Recruiter in this Chrome.
      </p>
    );
  }

  const update = Boolean(version);
  return (
    <section role="note" aria-label="Set up the FINN Outreach Sender" className="space-y-3 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-4 py-4 text-[13px] text-[#1A1A1A]">
      <div>
        <b className="text-sm">{update ? 'Please update the FINN Outreach Sender' : 'First, install the FINN Outreach Sender (one time, about 2 minutes)'}</b>
        <p className="m-0 mt-1 text-[#7A4B00]">
          {update
            ? `You have version ${version}, the newest is ${LATEST_EXTENSION_VERSION}. The old one may not work properly.`
            : 'It is a small helper for Chrome. It opens LinkedIn Recruiter for you, reads the candidates and puts your InMail into the message box. Without it, finding people and sending do not work.'}
        </p>
      </div>
      <ol className="m-0 list-none space-y-2.5 p-0">
        <Step n={1}>
          <a href={EXTENSION_ZIP} download className="font-semibold text-[#0072EA] underline">
            Click here to download it
          </a>
          . A file called <b>finn-outreach-sender.zip</b> lands in your <b>Downloads</b> folder.
        </Step>
        <Step n={2}>
          Open your <b>Downloads</b> folder and <b>double-click</b> the file (on Windows: right-click it › <b>Extract All</b>). A folder called <b>finn-outreach-sender</b> appears.{' '}
          {update ? (
            <>Put it where the old folder was and replace the old one.</>
          ) : (
            <>Leave this folder where it is — Chrome needs it. Do not delete it.</>
          )}
        </Step>
        <Step n={3}>
          Copy this address, paste it into the address bar at the top of Chrome and press Enter: <CopyAddress />
          <span className="block text-xs text-[#707070]">(Chrome does not let us link to it directly.) You now see a page called “Extensions”.</span>
        </Step>
        {update ? (
          <Step n={4}>
            Find <b>FINN Outreach Sender</b> on that page and click the round arrow <b>↻</b> on its tile.
          </Step>
        ) : (
          <>
            <Step n={4}>
              At the <b>top right</b> of that page, switch on <b>“Developer mode”</b> (in German: <b>„Entwicklermodus“</b>).
            </Step>
            <Step n={5}>
              At the <b>top left</b>, click <b>“Load unpacked”</b> (<b>„Entpackte Erweiterung laden“</b>) and choose the{' '}
              <b>finn-outreach-sender</b> folder from step 2. A tile <b>FINN Outreach Sender</b> with a blue paper-plane icon appears.
            </Step>
          </>
        )}
        <Step n={update ? 5 : 6}>
          Come back to this page and <b>reload it</b> (the round arrow ↻ next to the address bar, or <b>Cmd + R</b> on a Mac / <b>Ctrl + R</b> on Windows). This box turns into a green tick.
        </Step>
      </ol>
      <p className="m-0 text-xs text-[#7A4B00]">
        Stuck? Make sure you use <b>Chrome</b> (not Safari) and that you are signed in to <b>LinkedIn Recruiter</b> in this Chrome.
      </p>
    </section>
  );
}
